import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { withRouter } from 'react-router';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { axiosCaller } from '../../../utils/apiCaller';
import MikaPageHeader from '../../../components/MikaPageHeader/MikaPageHeader';
import MikaDateFilter, { DateRange } from '../../../components/MikaDateFilter/MikaDateFilter';
import MikaFileManager, { MikaFile } from '../../../components/MikaFileManager/MikaFileManager';
import CommentThread from './CommentThread';
import ProductRow from './ProductRow';
import moment from 'moment';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatPrice } from '../../../utils/react';
import '../../Contact/Contact.scss';
import '../../Offers/Offers.scss';
import './NewBoard.scss';

const CalendarHeader: React.FC<any> = ({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => {
    const intl = useIntl();
    const [showMonths, setShowMonths] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const year = date.getFullYear();
    const month = date.getMonth();

    const months = useMemo(() => {
        const fmt = new Intl.DateTimeFormat(intl.locale, { month: 'long' });
        return Array.from({ length: 12 }, (_, i) => {
            const name = fmt.format(new Date(2000, i, 1));
            return name.charAt(0).toUpperCase() + name.slice(1);
        });
    }, [intl.locale]);

    useEffect(() => {
        const cal = headerRef.current?.closest('.react-datepicker') as HTMLElement | null;
        if (showMonths) cal?.classList.add('month-picker-active');
        else {
            cal?.classList.remove('month-picker-active');
            const container = cal?.querySelector('.react-datepicker__month-container') as HTMLElement | null;
            if (container) container.style.minHeight = '';
        }
    }, [showMonths]);

    function openMonthPicker() {
        const cal = headerRef.current?.closest('.react-datepicker') as HTMLElement | null;
        const container = cal?.querySelector('.react-datepicker__month-container') as HTMLElement | null;
        if (container) container.style.minHeight = container.offsetHeight + 'px';
        setShowMonths(true);
    }

    if (showMonths) return (
        <div ref={headerRef} className="dp-header dp-header--months">
            <div className="dp-year-nav">
                <button type="button" className="dp-nav-btn" onClick={() => changeYear(year - 1)}>&#8249;</button>
                <span className="dp-year-label">{year}</span>
                <button type="button" className="dp-nav-btn" onClick={() => changeYear(year + 1)}>&#8250;</button>
            </div>
            <div className="dp-month-grid">
                {months.map((m, i) => (
                    <button type="button" key={m} className={`dp-month-btn${month === i ? ' active' : ''}`}
                        onClick={() => { changeMonth(i); setShowMonths(false); }}>{m}</button>
                ))}
            </div>
        </div>
    );
    return (
        <div ref={headerRef} className="dp-header">
            <button type="button" className="dp-nav-btn" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>&#8249;</button>
            <button type="button" className="dp-month-year-btn" onClick={openMonthPicker}>
                {months[month]} {year} <span className="dp-chevron">&#9662;</span>
            </button>
            <button type="button" className="dp-nav-btn" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>&#8250;</button>
        </div>
    );
};

interface Department { value: number; label: string; }
interface Responsibility { id: number; label: string; category?: 'activity' | 'production' | null; }
type CategoryValue = 'activity' | 'production';

const STATUS_MAP: Record<number, { key: string; label: string; color: string; bg: string }> = {
    1: { key: 'completed',         label: 'Përfunduar',       color: '#22c55e', bg: '#EAF8E8' },
    2: { key: 'toDo',              label: 'Për tu realizuar', color: '#3b82f6', bg: '#F6F9FF' },
    3: { key: 'late',              label: 'Me vonesë',        color: '#ef4444', bg: '#FAE7E7' },
    4: { key: 'inProcess',         label: 'Në proces',        color: '#eab308', bg: '#FCFCE5' },
    // The four pause reasons (5-8) are distinct responsibility_status rows, not one status with a
    // reason column — so each gets its own `key`. The key drives both the .npb-status-{key} CSS
    // class and the `production.status.{key}` i18n lookup, which is why the keys match the locale
    // keys exactly. They stay in the orange family (7 excepted, long-standing indigo) so pauses
    // still read as a group, but each shade is distinguishable at a glance on a dense board.
    5: { key: 'paused',            label: 'Pauzuar',          color: '#f97316', bg: '#FFF1E0' },
    6: { key: 'pausedEndOfShift',  label: 'Perfundimi orarit',color: '#d97706', bg: '#FDF3D9' },
    7: { key: 'otherWork',         label: 'Punë tjetër',      color: '#6366f1', bg: '#F0F0FF' },
    8: { key: 'pausedOther',       label: 'Arsye tjetër',     color: '#ea580c', bg: '#FFE4D6' },
    9: { key: 'nonActive',         label: 'Jo aktiv',         color: '#3b82f6', bg: 'linear-gradient(80deg, rgba(50,188,32,0.5) -5%, rgba(55,136,216,0.5) 100%)' },
};

export { STATUS_MAP };

// Renders a toolbar dropdown menu in a body-level portal so it escapes the mobile
// toolbar's `overflow-x: auto` clipping (and any stacking context). Positioned fixed
// under its trigger — same pattern as the datepicker portal.
const DropdownPortal: React.FC<{ anchorRef: React.RefObject<HTMLDivElement>; children: React.ReactNode }> = ({ anchorRef, children }) => {
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    useLayoutEffect(() => {
        const el = anchorRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setPos({ top: r.bottom, left: Math.min(r.left, window.innerWidth - 212) });
    }, [anchorRef]);
    if (!pos) return null;
    return ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 3000 }} onClick={e => e.stopPropagation()}>
            {children}
        </div>,
        // Portal into #root (not <body>) so the `#root .npb-dropdown-item` styles
        // still apply — the menu keeps its original look and still escapes the
        // toolbar's overflow clip.
        document.getElementById('root') || document.body
    );
};

const NewProductionBoard: React.FC<{ history?: any }> = ({ history }) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string, values?: Record<string, any>) =>
            intl.formatMessage({ id, defaultMessage }, values),
        [intl]
    );

    // Redux: populated by Production.tsx mount via getUserPermissionsAndResponsibilities.
    // Already in store by the time this child mounts.
    const permissions = useSelector((state: any) => state?.app?.permissions || {});
    const adminDepartments = useSelector((state: any) => state?.app?.adminDepartments || []);

    const [products, setProducts] = useState<any[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [responsibilities, setResponsibilities] = useState<Responsibility[]>([]);
    const [selectedDeptIds, setSelectedDeptIds] = useState<string[]>([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedBox, setSelectedBox] = useState<{ productId: number; responsibilityId: number } | null>(null);

    // Admin view: true if fullPrivileges, otherwise true only when every selected
    // dept is one the user admins ("every" rule). "All" view (no selection) is
    // simple-user — admin powers don't span depts.
    const isAdmin = useMemo(() => {
        if (permissions?.fullPrivileges) return true;
        if (!selectedDeptIds.length) return false;
        return selectedDeptIds.every((id) =>
            adminDepartments?.some((d: any) => Number(d?.departmentId) === Number(id) && d?.isAdmin)
        );
    }, [permissions, adminDepartments, selectedDeptIds]);

    // Deep-link state — set once when arriving from a notification's action_url
    // and consumed by render passes (pulse animation, ExpansionPanel material
    // pre-select). The deepLinkConsumedRef guards against repeated handling
    // if products refetch after the initial mount.
    const [flashProductId, setFlashProductId] = useState<number | null>(null);
    const [pendingMaterialId, setPendingMaterialId] = useState<number | null>(null);
    const deepLinkConsumedRef = useRef(false);
    const categoryWrapRef = useRef<HTMLDivElement>(null);
    const processWrapRef = useRef<HTMLDivElement>(null);
    const statusWrapRef = useRef<HTMLDivElement>(null);
    const sortWrapRef = useRef<HTMLDivElement>(null);

    // Notification deep-link handler.
    //
    // URL contract (set by notification templates, see DefaultNotificationRulesSeeder):
    //   ?productId=X&responsibilityId=Y[&materialId=Z]&open=1      → comments: open the panel
    //   ?productId=X&responsibilityId=Y&highlight=1                → status events: scroll + pulse only
    //
    // Effect waits for the initial product fetch to land, then:
    //   1. Finds the target product. Not found → silent no-op (deleted/archived).
    //   2. If filters would hide it, clears them so the row renders.
    //   3. Scrolls the row into the viewport's centre.
    //   4. Flashes the row for ~2.5s via .is-flashing class (CSS animation).
    //   5. If open=1, opens the ExpansionPanel + stashes materialId for the
    //      child to consume on its mount-time useEffect.
    //   6. Strips the query params from the URL so a refresh doesn't re-trigger.
    //
    // The consumed ref prevents re-running on data refetches (smart polling
    // rewrites `products` every ~30s — without the guard, every refetch would
    // re-scroll + re-flash, which is annoying).
    useEffect(() => {
        if (deepLinkConsumedRef.current) return;
        if (loading || products.length === 0) return;

        const params = new URLSearchParams(window.location.search);
        const productIdStr = params.get('productId');
        if (!productIdStr) return;

        const productIdNum = parseInt(productIdStr, 10);
        const responsibilityIdNum = parseInt(params.get('responsibilityId') || '', 10);
        const materialIdRaw = params.get('materialId');
        const materialIdNum = materialIdRaw && /^\d+$/.test(materialIdRaw) ? parseInt(materialIdRaw, 10) : null;
        const shouldOpen = params.get('open') === '1';

        if (Number.isNaN(productIdNum)) {
            deepLinkConsumedRef.current = true;
            return;
        }

        // Find the product in the full set (filteredProducts might hide it).
        const target = products.find((p: any) => p.productId === productIdNum);
        if (!target) {
            // Product not in the current board scope at all (deleted, archived,
            // or simply not loaded — e.g. admin user clicking a notification for
            // a non-admin scope). Mark consumed so we don't loop on refetches.
            deepLinkConsumedRef.current = true;
            if (history && typeof history.replace === 'function') {
                history.replace('/dashboard/production/board');
            }
            return;
        }

        // Filter override: clear dept + search so the row actually renders.
        // We don't try to be surgical — clearing everything is more predictable
        // than guessing which filter was hiding the product.
        if (selectedDeptIds.length > 0) setSelectedDeptIds([]);
        if (search !== '') { setSearch(''); setDebouncedSearch(''); }

        // Schedule the visual operations for the NEXT frame, after the filter
        // reset has flushed and the row is in the DOM.
        requestAnimationFrame(() => {
            const el = document.querySelector(`[data-product-id="${productIdNum}"]`) as HTMLElement | null;
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }

            // Flash for 2.5s — CSS animation runs as long as the class is present.
            setFlashProductId(productIdNum);
            window.setTimeout(() => setFlashProductId((curr) => (curr === productIdNum ? null : curr)), 2500);

            // Auto-open the ExpansionPanel for comment notifications. Status
            // events don't pass open=1, so they just get the highlight.
            if (shouldOpen && !Number.isNaN(responsibilityIdNum)) {
                setSelectedBox({ productId: productIdNum, responsibilityId: responsibilityIdNum });
                setPendingMaterialId(materialIdNum);
                // Clear pendingMaterialId after the ExpansionPanel mounts and
                // consumes it on its mount-time useEffect (~100ms is plenty).
                window.setTimeout(() => setPendingMaterialId(null), 200);
            }
        });

        // Strip query params so a manual refresh doesn't replay this.
        if (history && typeof history.replace === 'function') {
            history.replace('/dashboard/production/board');
        }
        deepLinkConsumedRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, products.length]);

    const [userResponsibilityIds, setUserResponsibilityIds] = useState<number[]>([]);
    const [productionFlowMode, setProductionFlowMode] = useState<'linear' | 'free'>('linear');
    const [authReady, setAuthReady] = useState(false);

    const [detailProduct, setDetailProduct] = useState<any>(null);
    const [confirmPassive, setConfirmPassive] = useState(false);
    const [detailFiles, setDetailFiles] = useState<MikaFile[]>([]);
    const [detailFilesLoading, setDetailFilesLoading] = useState(false);
    const [detailStartDate, setDetailStartDate] = useState('');
    const [detailEndDate, setDetailEndDate] = useState('');
    const [detailDateComment, setDetailDateComment] = useState('');

    const detailStartSelected = useMemo(
        () => detailStartDate ? new Date(detailStartDate)
            : (detailProduct?.start ? moment(detailProduct.start).toDate() : null),
        [detailStartDate, detailProduct?.start]
    );
    const detailEndSelected = useMemo(
        () => detailEndDate ? new Date(detailEndDate)
            : (detailProduct?.end ? moment(detailProduct.end).toDate() : null),
        [detailEndDate, detailProduct?.end]
    );
    const isSameYMD = (a: Date | null, b: Date | null) =>
        !!a && !!b && a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

    const detailToday = useMemo(() => moment().startOf('day').toDate(), [detailProduct?.productId]);

    const [detailStartView, setDetailStartView] = useState<Date | null>(null);
    const [detailEndView, setDetailEndView] = useState<Date | null>(null);
    useEffect(() => {
        if (detailProduct) {
            setDetailStartView(detailStartSelected ?? new Date());
            setDetailEndView(detailEndSelected ?? new Date());
        } else {
            setDetailStartView(null);
            setDetailEndView(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailProduct]);

    // Keep the open popup in sync with the products array — so saves and background
    // polls refresh the visible data without forcing the user to close + reopen.
    useEffect(() => {
        if (!detailProduct) return;
        const updated = products.find(p => p.productId === detailProduct.productId);
        if (updated) setDetailProduct(updated);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products]);

    const STATUS_FILTER_OPTIONS = useMemo(() => [
        { value: '1', label: fm('production.status.completed', 'Përfunduar') },
        { value: '2', label: fm('production.status.toDo', 'Për tu realizuar') },
        { value: '3', label: fm('production.status.late', 'Me vonesë') },
        { value: '4', label: fm('production.status.inProcess', 'Në proces') },
        // Each pause reason is filterable on its own. Previously only id 5 had an option, so
        // "Pauzuar" silently excluded processes paused for end-of-shift (6), other work (7) or
        // another reason (8) — which is why the filter appeared to return almost nothing.
        { value: '5', label: fm('production.status.paused', 'Pauzuar') },
        { value: '6', label: fm('production.status.pausedEndOfShift', 'Përfundimi orarit') },
        { value: '7', label: fm('production.status.otherWork', 'Punë tjetër') },
        { value: '8', label: fm('production.status.pausedOther', 'Arsye tjetër') },
        { value: '9', label: fm('production.status.nonActive', 'Jo aktiv') },
    ], [fm]);

    const FILTER_DATE_BY_OPTIONS = useMemo(() => [
        { value: 'start', label: fm('production.board.filterByStart', 'Datës së fillimit') },
        { value: 'end',   label: fm('production.board.filterByEnd',   'Datës së mbarimit') },
    ], [fm]);

    const weekdayNarrow = useCallback((d: string) => {
        const idx = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].indexOf(d);
        if (idx < 0) return d;
        // Jan 7 2024 is a Sunday → add idx days to get correct weekday.
        const date = new Date(2024, 0, 7 + idx);
        return new Intl.DateTimeFormat(intl.locale, { weekday: 'narrow' }).format(date);
    }, [intl.locale]);

    const saveDetailDates = async () => {
        if (!detailDateComment.trim()) {
            toast.info(fm('production.toast.dateChangeReasonRequired', 'Ju lutem shkruani arsyen e ndryshimit të datave!'));
            return;
        }
        const startVal = detailStartDate || detailProduct?.start;
        const endVal = detailEndDate || detailProduct?.end;
        const todayMs = moment().startOf('day').valueOf();
        if (detailStartDate && new Date(detailStartDate).getTime() < todayMs) {
            toast.error(fm('production.toast.dateInPast', 'Datat nuk mund të jenë në të kaluarën!'));
            return;
        }
        if (detailEndDate && new Date(detailEndDate).getTime() < todayMs) {
            toast.error(fm('production.toast.dateInPast', 'Datat nuk mund të jenë në të kaluarën!'));
            return;
        }
        if (startVal && endVal && new Date(startVal).getTime() > new Date(endVal).getTime()) {
            toast.error(fm('production.toast.endBeforeStart', 'Data e mbarimit nuk mund të jetë para fillimit!'));
            return;
        }
        try {
            await axiosCaller('POST', 'admin/products/change-start-end-date', {
                productId: detailProduct.productId,
                start: startVal ? moment(startVal).format('YYYY-MM-DD') : null,
                end: endVal ? moment(endVal).format('YYYY-MM-DD') : null,
                calendarComment: detailDateComment.trim(),
            });
            toast.success(fm('production.toast.datesChanged', 'Datat u ndryshuan!'));
            setDetailDateComment('');
            lastFetchTimestamp.current = null;
            fetchProductsFull();
        } catch {
            toast.error(fm('production.toast.somethingWentWrong', 'Diçka nuk shkoi mirë!'));
        }
    };

    const openProductDetail = (product: any) => {
        setDetailProduct(product);
        setConfirmPassive(false);
        setDetailStartDate('');
        setDetailEndDate('');
        setDetailDateComment('');
        setDetailFilesLoading(true);
        axiosCaller('GET', `project-blueprint/${product.productId}/products`).then(res => {
            const files = (res?.data?.data || []).map((f: any) => ({
                id: f.id,
                name: f.nameOfBlueprint || 'file',
                url: f.blueprint,
            }));
            setDetailFiles(files);
            setDetailFilesLoading(false);
        }).catch(() => setDetailFilesLoading(false));
    };

    const closeProductDetail = () => {
        setDetailProduct(null);
        setDetailFiles([]);
        setConfirmPassive(false);
    };

    // Make the whole product passive (product info popup, admin only). Uses the
    // product-level endpoint which sets products.passive and cascades to every
    // product_status.active; the product then drops off the active board into the
    // passive list. Reactivation lives on the passive-products page.
    const makeProductPassive = async () => {
        if (!detailProduct) return;
        try {
            await axiosCaller('POST', `admin/products/${detailProduct.productId}/passive`, { passive: 1 });
            toast.success(fm('production.expansion.madePassive', 'Produkti u bë pasiv!'));
            setConfirmPassive(false);
            closeProductDetail();
            lastFetchTimestamp.current = null;
            fetchProductsFull();
        } catch {
            toast.error(fm('production.toast.somethingWentWrong', 'Diçka nuk shkoi mirë!'));
        }
    };

    const [zoomLvl, setZoomLvl] = useState(100);
    const controlZoom = (increase: boolean) => {
        setZoomLvl(prev => increase ? Math.min(prev + 10, 150) : Math.max(prev - 10, 10));
    };

    const [colWidths, setColWidths] = useState({ client: 100, project: 100, product: 175, qty: 50 });
    const resizing = useRef<{ col: string; startX: number; startWidth: number } | null>(null);

    const onResizeStart = (col: string, e: React.MouseEvent) => {
        e.preventDefault();
        resizing.current = { col, startX: e.clientX, startWidth: (colWidths as any)[col] };
        const onMove = (ev: MouseEvent) => {
            if (!resizing.current) return;
            const diff = ev.clientX - resizing.current.startX;
            const newWidth = Math.max(50, resizing.current.startWidth + diff);
            setColWidths(prev => ({ ...prev, [resizing.current!.col]: newWidth }));
        };
        const onUp = () => { resizing.current = null; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    const [statusFilters, setStatusFilters] = useState<string[]>([]);
    const [responsibilityFilters, setResponsibilityFilters] = useState<string[]>([]);
    const [categoryFocus, setCategoryFocus] = useState<CategoryValue[]>([]);
    const [filterDatesBy, setFilterDatesBy] = useState('end');
    const [dateRange, setDateRange] = useState<DateRange | null>(null);
    const [sortBy, setSortBy] = useState('end');

    const [processDropOpen, setProcessDropOpen] = useState(false);
    const [statusDropOpen, setStatusDropOpen] = useState(false);
    const [sortDropOpen, setSortDropOpen] = useState(false);
    const [categoryDropOpen, setCategoryDropOpen] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    const hasActiveFilters = statusFilters.length > 0 || responsibilityFilters.length > 0 || dateRange || categoryFocus.length > 0;

    useEffect(() => {
        axiosCaller('GET', 'all-departments').then(res => {
            setDepartments((res?.data?.data || []).map((d: any) => ({ value: d.value || d.id, label: d.label || d.name })));
        });
        axiosCaller('GET', 'responsibilities').then(res => {
            setResponsibilities((res?.data?.data || []).map((r: any) => ({ id: r.id || r.value, label: r.label || r.name, category: r.category ?? null })));
        });

        axiosCaller('GET', 'authenticated').then(res => {
            setUserResponsibilityIds(res?.data?.responsibilities || []);
            setProductionFlowMode(res?.data?.productionFlowMode === 'free' ? 'free' : 'linear');
            // Company-configured default row order (admin/production-settings → board_sort). Applied
            // before authReady flips, so the very first fetch already uses it. The sort dropdown
            // below still overrides it for the session.
            const boardSort = res?.data?.productionBoardSort;
            if (['end', 'start', 'created'].includes(boardSort)) setSortBy(boardSort);
            setAuthReady(true);
        }).catch(() => setAuthReady(true));

        const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelectedBox(null); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const lastFetchTimestamp = useRef<string | null>(null);
    const fullRefreshCount = useRef(0);
    const pollingTimeout = useRef<any>(null);
    const isMounted = useRef(true);
    const filterQsRef = useRef('');

    const buildFilterQs = useCallback(() => {
        let qs = '';
        selectedDeptIds.forEach(id => { qs += `&departmentId[]=${id}`; });
        if (debouncedSearch) qs += `&name=${encodeURIComponent(debouncedSearch)}`;
        statusFilters.forEach(id => { qs += `&responsibilityStatusId[]=${id}`; });
        responsibilityFilters.forEach(id => { qs += `&responsibilityId[]=${id}`; });
        if (dateRange?.from) qs += `&start=${dateRange.from}`;
        if (dateRange?.to) qs += `&end=${dateRange.to}`;
        if ((dateRange?.from || dateRange?.to) && filterDatesBy) qs += `&filterDatesBy=${filterDatesBy}`;
        if (sortBy) qs += `&sort=${sortBy}`;
        if (!isAdmin) qs += '&forUser=1';
        filterQsRef.current = qs;
        return qs;
    }, [isAdmin, selectedDeptIds, debouncedSearch, statusFilters, responsibilityFilters, dateRange, filterDatesBy, sortBy]);

    const fetchProductsFull = useCallback(async () => {
        const qs = `clients/products-statuses?moduleId=5&active=1&done=0${filterQsRef.current}`;
        try {
            const res = await axiosCaller('GET', qs);
            const raw = res?.data?.data || res?.data || [];
            const arr = Array.isArray(raw) ? raw : [];
            const transformed = arr.map((p: any) => transformProduct(p)).filter((p: any) => p.statuses.length > 0);
            setProducts(transformed);
            lastFetchTimestamp.current = new Date().toISOString();
            fullRefreshCount.current = 0;
        } catch {}
        setLoading(false);
    }, []);

    const fetchProductsChanges = useCallback(async () => {
        if (!lastFetchTimestamp.current) { await fetchProductsFull(); return; }

        const qs = `clients/products-statuses-changes?moduleId=5&active=1&done=0&since=${encodeURIComponent(lastFetchTimestamp.current)}${filterQsRef.current}`;
        try {
            const res = await axiosCaller('GET', qs);
            const changed = res?.data?.data || res?.data || [];
            const changedArr = Array.isArray(changed) ? changed : [];

            if (changedArr.length > 0) {
                setProducts(prev => {
                    const updated = [...prev];
                    changedArr.forEach((cp: any) => {
                        const transformed = transformProduct(cp);
                        if (transformed.statuses.length === 0) return;
                        const idx = updated.findIndex(p => p.productId === transformed.productId);
                        if (idx >= 0) { updated[idx] = transformed; }
                        else { updated.push(transformed); }
                    });
                    return updated;
                });
            }
            lastFetchTimestamp.current = new Date().toISOString();
        } catch {}
    }, [fetchProductsFull]);

    const poll = useCallback(async () => {
        if (!isMounted.current) return;
        fullRefreshCount.current += 1;
        if (fullRefreshCount.current >= 20) { await fetchProductsFull(); }
        else { await fetchProductsChanges(); }
        if (isMounted.current) { pollingTimeout.current = setTimeout(poll, 10000); }
    }, [fetchProductsFull, fetchProductsChanges]);

    useEffect(() => {
        if (!authReady) return;

        buildFilterQs();

        isMounted.current = true;
        lastFetchTimestamp.current = null;
        fullRefreshCount.current = 0;
        if (pollingTimeout.current) clearTimeout(pollingTimeout.current);

        setLoading(true);
        setProducts([]);
        fetchProductsFull().then(() => {
            if (isMounted.current) {
                pollingTimeout.current = setTimeout(poll, 10000);
            }
        });

        return () => {
            isMounted.current = false;
            if (pollingTimeout.current) clearTimeout(pollingTimeout.current);
        };
    }, [buildFilterQs, authReady]);

    const transformProduct = (p: any) => {
        const latestComments = p.latestComments || {};
        const statuses = (p.statuses || []).map((s: any) => {
            const responsibilityId = s.responsibility?.id;
            const materialStatuses = (p.materialStatuses || []).filter(
                (m: any) => m.responsibilityId === responsibilityId
            ).map((m: any) => ({ ...m, trackers: m.trackers || null }));
            const hasMaterials = materialStatuses.length > 0;
            const completedMaterials = materialStatuses.filter((m: any) => m.responsibilityStatusId === 1).length;
            const latestComment = latestComments[`${responsibilityId}_0`] || null;
            return {
                id: s.id,
                responsibilityId,
                responsibilityName: s.responsibility?.label,
                responsibilityStatusId: s.responsibilityStatusId,
                statusInfo: STATUS_MAP[s.responsibilityStatusId] || STATUS_MAP[9],
                userName: s.responsibility?.userName,
                updatedAt: s.updatedAt,
                warning: s.warning,
                adminWarning: s.adminWarning,
                // Free-text reason captured when a process is paused with "Arsye tjetër" (status 8).
                // ProductStatusResource has always returned it; it was simply never mapped here, so
                // the modal had no way to show why the process was paused.
                otherComment: s.otherComment || null,
                returnCount: s.returnCount || 0,
                latestComment,
                hasMaterials,
                materialCount: materialStatuses.length,
                completedMaterials,
                materialStatuses,
            };
        });
        return {
            productId: p.id,
            clientId: p.client?.id,
            clientName: p.client?.name,
            projectId: p.project?.id,
            projectName: p.project?.name,
            productName: p.designation?.name,
            productCode: p.type,
            quantity: p.quantity,
            unit: p.designation?.unit?.name,
            start: p.start,
            end: p.end,
            calendarComment: p.calendarComment,
            offerBy: p.offerCreated?.userName,
            measureStatus: p.measureStatus,
            totalPrice: p.discountedPrice || p.totalPrice,
            completedProcesses: p.completedProcesses,
            totalProcesses: p.totalProcesses,
            productionStartedAt: p.productionStartedAt,
            statuses,
        };
    };


    const clearFilters = () => {
        setStatusFilters([]);
        setResponsibilityFilters([]);
        setCategoryFocus([]);
        setDateRange(null);
        setFilterDatesBy('end');
    };

    const toggleMultiFilter = (list: string[], setList: (v: string[]) => void, value: string) => {
        setList(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);
    };

    const toggleCategoryFocus = (value: CategoryValue) => {
        setCategoryFocus(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    };

    const categoryByRespId = useMemo(() => {
        const map = new Map<number, CategoryValue | null>();
        responsibilities.forEach(r => map.set(r.id, (r.category ?? null) as CategoryValue | null));
        return map;
    }, [responsibilities]);

    // Category filter is a real filter (not a dim). A status box is visible only
    // when its responsibility's category is in the active selection.
    const isResponsibilityVisible = useCallback((responsibilityId: number) => {
        if (categoryFocus.length === 0) return true;
        const cat = categoryByRespId.get(responsibilityId) ?? null;
        return !!cat && categoryFocus.includes(cat);
    }, [categoryFocus, categoryByRespId]);

    // Row filter: when a category is selected, show only products in that
    // category's lens — work STARTED (a status != 2) AND NOT fully done (a
    // status != 1) within the category. Mirrors the ordered-products page.
    const filteredProducts = useMemo(() => {
        if (categoryFocus.length === 0) return products;
        return products.filter((product: any) =>
            categoryFocus.some(cat => {
                const catStatuses = (product.statuses || []).filter((s: any) => categoryByRespId.get(s.responsibilityId) === cat);
                if (catStatuses.length === 0) return false;
                const started = catStatuses.some((s: any) => s.responsibilityStatusId != null && s.responsibilityStatusId !== 2);
                const notAllDone = catStatuses.some((s: any) => s.responsibilityStatusId == null || s.responsibilityStatusId !== 1);
                return started && notAllDone;
            })
        );
    }, [products, categoryFocus, categoryByRespId]);

    const CATEGORY_OPTIONS: { value: CategoryValue; labelId: string; defaultLabel: string }[] = [
        { value: 'activity',   labelId: 'production.board.category.activity',   defaultLabel: 'Aktivitet (Projektimi)' },
        { value: 'production', labelId: 'production.board.category.production', defaultLabel: 'Procese të prodhimit' },
    ];

    useEffect(() => {
        const handler = () => { setProcessDropOpen(false); setStatusDropOpen(false); setSortDropOpen(false); setCategoryDropOpen(false); };
        if (processDropOpen || statusDropOpen || sortDropOpen || categoryDropOpen) {
            setTimeout(() => document.addEventListener('click', handler), 0);
            return () => document.removeEventListener('click', handler);
        }
    }, [processDropOpen, statusDropOpen, sortDropOpen, categoryDropOpen]);

    return (
        <div className="npb-container">
            <MikaPageHeader
                title={fm('production.list.title', 'Prodhimi')}
                subtitle={`${filteredProducts.length} ${fm('production.board.subtitleProducts', 'produkte')}`}
            />

            <div className="npb-dept-strip">
                <button className={`npb-dept-btn ${selectedDeptIds.length === 0 ? 'active' : ''}`} onClick={() => setSelectedDeptIds([])} type="button">
                    <FormattedMessage id="common.pagination.all" defaultMessage="Të gjitha" />
                </button>
                {departments.map(d => (
                    <button key={d.value} className={`npb-dept-btn ${selectedDeptIds.includes(String(d.value)) ? 'active' : ''}`} onClick={() => toggleMultiFilter(selectedDeptIds, setSelectedDeptIds, String(d.value))} type="button">
                        {d.label}
                    </button>
                ))}
            </div>

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input type="text" className="clp-search-input" placeholder={fm('production.board.searchPlaceholder', 'Kërko produkte...')}
                        value={search} onChange={e => setSearch(e.target.value)} />
                    {search && (
                        <button className="clp-search-clear" onClick={() => setSearch('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <div className="npb-dropdown-wrap" ref={categoryWrapRef} onClick={e => e.stopPropagation()}>
                        <button className={`npb-toolbar-btn ${categoryDropOpen ? 'active' : ''} ${categoryFocus.length > 0 ? 'has-value' : ''}`} onClick={() => { setCategoryDropOpen(!categoryDropOpen); setProcessDropOpen(false); setStatusDropOpen(false); setSortDropOpen(false); }} type="button">
                            <FormattedMessage id="production.board.categories" defaultMessage="Kategoritë" />
                            {categoryFocus.length > 0 && <span className="npb-toolbar-badge">{categoryFocus.length}</span>}
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                        </button>
                        {categoryDropOpen && (
                            <DropdownPortal anchorRef={categoryWrapRef}>
                            <div className="npb-dropdown npb-dropdown-multi">
                                {CATEGORY_OPTIONS.map(c => (
                                    <button key={c.value} className={`npb-dropdown-item ${categoryFocus.includes(c.value) ? 'active' : ''}`} onClick={() => { toggleCategoryFocus(c.value); setCategoryDropOpen(false); }} type="button">
                                        <span className="npb-dropdown-check">{categoryFocus.includes(c.value) ? '✓' : ''}</span>
                                        {fm(c.labelId, c.defaultLabel)}
                                    </button>
                                ))}
                                {categoryFocus.length > 0 && (
                                    <button className="npb-dropdown-clear" onClick={() => setCategoryFocus([])} type="button">
                                        <FormattedMessage id="production.board.clear" defaultMessage="Pastro" />
                                    </button>
                                )}
                            </div>
                            </DropdownPortal>
                        )}
                    </div>
                    <div className="npb-dropdown-wrap" ref={processWrapRef} onClick={e => e.stopPropagation()}>
                        <button className={`npb-toolbar-btn ${processDropOpen ? 'active' : ''} ${responsibilityFilters.length > 0 ? 'has-value' : ''}`} onClick={() => { setProcessDropOpen(!processDropOpen); setStatusDropOpen(false); setSortDropOpen(false); setCategoryDropOpen(false); }} type="button">
                            <FormattedMessage id="production.board.processes" defaultMessage="Proceset" />
                            {responsibilityFilters.length > 0 && <span className="npb-toolbar-badge">{responsibilityFilters.length}</span>}
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                        </button>
                        {processDropOpen && (
                            <DropdownPortal anchorRef={processWrapRef}>
                            <div className="npb-dropdown npb-dropdown-multi">
                                {responsibilities.map(r => (
                                    <button key={r.id} className={`npb-dropdown-item ${responsibilityFilters.includes(String(r.id)) ? 'active' : ''}`} onClick={() => toggleMultiFilter(responsibilityFilters, setResponsibilityFilters, String(r.id))} type="button">
                                        <span className="npb-dropdown-check">{responsibilityFilters.includes(String(r.id)) ? '✓' : ''}</span>
                                        {r.label}
                                    </button>
                                ))}
                                {responsibilityFilters.length > 0 && (
                                    <button className="npb-dropdown-clear" onClick={() => setResponsibilityFilters([])} type="button">
                                        <FormattedMessage id="production.board.clear" defaultMessage="Pastro" />
                                    </button>
                                )}
                            </div>
                            </DropdownPortal>
                        )}
                    </div>

                    <div className="npb-dropdown-wrap" ref={statusWrapRef} onClick={e => e.stopPropagation()}>
                        <button className={`npb-toolbar-btn ${statusDropOpen ? 'active' : ''} ${statusFilters.length > 0 ? 'has-value' : ''}`} onClick={() => { setStatusDropOpen(!statusDropOpen); setProcessDropOpen(false); setSortDropOpen(false); setCategoryDropOpen(false); }} type="button">
                            <FormattedMessage id="production.board.statuses" defaultMessage="Statuset" />
                            {statusFilters.length > 0 && <span className="npb-toolbar-badge">{statusFilters.length}</span>}
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                        </button>
                        {statusDropOpen && (
                            <DropdownPortal anchorRef={statusWrapRef}>
                            <div className="npb-dropdown npb-dropdown-multi">
                                {STATUS_FILTER_OPTIONS.map(o => (
                                    <button key={o.value} className={`npb-dropdown-item ${statusFilters.includes(o.value) ? 'active' : ''}`} onClick={() => toggleMultiFilter(statusFilters, setStatusFilters, o.value)} type="button">
                                        <span className="npb-dropdown-check">{statusFilters.includes(o.value) ? '✓' : ''}</span>
                                        {o.label}
                                    </button>
                                ))}
                                {statusFilters.length > 0 && (
                                    <button className="npb-dropdown-clear" onClick={() => setStatusFilters([])} type="button">
                                        <FormattedMessage id="production.board.clear" defaultMessage="Pastro" />
                                    </button>
                                )}
                            </div>
                            </DropdownPortal>
                        )}
                    </div>

                    <div className="npb-dropdown-wrap" ref={sortWrapRef} onClick={e => e.stopPropagation()}>
                        <button className={`npb-toolbar-btn ${sortDropOpen ? 'active' : ''}`} onClick={() => { setSortDropOpen(!sortDropOpen); setProcessDropOpen(false); setStatusDropOpen(false); setCategoryDropOpen(false); }} type="button">
                            <FormattedMessage id="production.board.sortBy" defaultMessage="Renditja" />
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                        </button>
                        {sortDropOpen && (
                            <DropdownPortal anchorRef={sortWrapRef}>
                            <div className="npb-dropdown">
                                <button className={`npb-dropdown-item ${sortBy === 'end' ? 'active' : ''}`} onClick={() => { setSortBy('end'); setSortDropOpen(false); }} type="button">
                                    <FormattedMessage id="production.board.sortByEnd" defaultMessage="Datës së mbarimit" />
                                </button>
                                <button className={`npb-dropdown-item ${sortBy === 'start' ? 'active' : ''}`} onClick={() => { setSortBy('start'); setSortDropOpen(false); }} type="button">
                                    <FormattedMessage id="production.board.sortByStart" defaultMessage="Datës së fillimit" />
                                </button>
                                <button className={`npb-dropdown-item ${sortBy === 'created' ? 'active' : ''}`} onClick={() => { setSortBy('created'); setSortDropOpen(false); }} type="button">
                                    <FormattedMessage id="production.board.sortByCreated" defaultMessage="Datës së krijimit" />
                                </button>
                            </div>
                            </DropdownPortal>
                        )}
                    </div>

                    <MikaDateFilter
                        value={dateRange}
                        onChange={(r) => setDateRange(r)}
                        filterBy={filterDatesBy}
                        onFilterByChange={setFilterDatesBy}
                        filterByOptions={FILTER_DATE_BY_OPTIONS}
                    />

                    {hasActiveFilters && (
                        <button className="npb-clear-all" onClick={clearFilters} type="button" title={fm('production.board.clearFilters', 'Pastro filtrat')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}

                    <div className="npb-zoom-control">
                        <button type="button" className="npb-zoom-btn" onClick={() => controlZoom(false)} title={fm('production.board.zoomOut', 'Zvogëlo')}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM136 184c-13.3 0-24 10.7-24 24s10.7 24 24 24H280c13.3 0 24-10.7 24-24s-10.7-24-24-24H136z"/></svg>
                        </button>
                        <span className="npb-zoom-label">{zoomLvl}%</span>
                        <button type="button" className="npb-zoom-btn" onClick={() => controlZoom(true)} title={fm('production.board.zoomIn', 'Zmadho')}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM184 296c0 13.3 10.7 24 24 24s24-10.7 24-24V232h64c13.3 0 24-10.7 24-24s-10.7-24-24-24H232V120c0-13.3-10.7-24-24-24s-24 10.7-24 24v64H120c-13.3 0-24 10.7-24 24s10.7 24 24 24h64v64z"/></svg>
                        </button>
                    </div>
                </div>
            </div>

            <div className="npb-board">
                <div className="npb-board-inner" style={{ zoom: `${zoomLvl}%` }}>
                <div className="npb-header">
                    <div className="npb-header-sticky">
                        <span className="npb-header-cell npb-col-num">#</span>
                        <span className="npb-header-cell" style={{ width: colWidths.client, minWidth: 50 }}>
                            <FormattedMessage id="production.board.clientCol" defaultMessage="Klienti" />
                            <span className="npb-resize-handle" onMouseDown={e => onResizeStart('client', e)} />
                        </span>
                        <span className="npb-header-cell" style={{ width: colWidths.project, minWidth: 50 }}>
                            <FormattedMessage id="production.board.projectCol" defaultMessage="Projekti" />
                            <span className="npb-resize-handle" onMouseDown={e => onResizeStart('project', e)} />
                        </span>
                        <span className="npb-header-cell" style={{ width: colWidths.product, minWidth: 50 }}>
                            <FormattedMessage id="production.board.productCol" defaultMessage="Produkti" />
                            <span className="npb-resize-handle" onMouseDown={e => onResizeStart('product', e)} />
                        </span>
                        <span className="npb-header-cell" style={{ width: colWidths.qty, minWidth: 50 }}>
                            <FormattedMessage id="production.board.quantityCol" defaultMessage="Sasia" />
                            <span className="npb-resize-handle" onMouseDown={e => onResizeStart('qty', e)} />
                        </span>
                    </div>
                    <span className="npb-header-cell npb-col-statuses">
                        <FormattedMessage id="production.board.processesCol" defaultMessage="Proceset" />
                    </span>
                </div>

                {loading ? (
                    <div className="npb-loading">
                        <div className="npb-spinner" />
                        <span><FormattedMessage id="production.board.loading" defaultMessage="Duke ngarkuar..." /></span>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="npb-empty"><FormattedMessage id="common.noData" defaultMessage="Nuk ka të dhëna" /></div>
                ) : (
                    <div className="npb-rows">
                        {filteredProducts.map((product, index) => (
                            <ProductRow
                                key={product.productId}
                                product={product}
                                index={index}
                                isAdmin={isAdmin}
                                userResponsibilityIds={userResponsibilityIds}
                                flowMode={productionFlowMode}
                                colWidths={colWidths}
                                selectedBox={selectedBox}
                                isResponsibilityVisible={isResponsibilityVisible}
                                onRowClick={() => openProductDetail(product)}
                                onBoxClick={(responsibilityId: number) => {
                                    const isSame = selectedBox?.productId === product.productId && selectedBox?.responsibilityId === responsibilityId;
                                    setSelectedBox(isSame ? null : { productId: product.productId, responsibilityId });
                                }}
                                onClose={() => setSelectedBox(null)}
                                onStatusUpdated={() => { lastFetchTimestamp.current = null; fetchProductsFull(); }}
                                isFlashing={flashProductId === product.productId}
                                initialMaterialId={
                                    selectedBox?.productId === product.productId ? pendingMaterialId : null
                                }
                            />
                        ))}
                    </div>
                )}
                </div>
            </div>

            <div className="npb-legend">
                <span className="npb-legend-item"><span className="npb-legend-dot npb-legend-completed" /><FormattedMessage id="production.status.completed" defaultMessage="Përfunduar" /></span>
                <span className="npb-legend-item"><span className="npb-legend-dot npb-legend-toDo" /><FormattedMessage id="production.status.toDo" defaultMessage="Për tu realizuar" /></span>
                <span className="npb-legend-item"><span className="npb-legend-dot npb-legend-late" /><FormattedMessage id="production.status.late" defaultMessage="Me vonesë" /></span>
                <span className="npb-legend-item"><span className="npb-legend-dot npb-legend-inProcess" /><FormattedMessage id="production.status.inProcess" defaultMessage="Në proces" /></span>
                <span className="npb-legend-item"><span className="npb-legend-dot npb-legend-paused" /><FormattedMessage id="production.status.paused" defaultMessage="Pauzuar" /></span>
                <span className="npb-legend-item"><span className="npb-legend-dot npb-legend-nonActive" /><FormattedMessage id="production.status.nonActive" defaultMessage="Jo aktiv" /></span>
            </div>

            {detailProduct && (
                <div className="npb-detail-overlay" onClick={closeProductDetail}>
                    <div className="npb-detail-popup" onClick={e => e.stopPropagation()}>
                        <div className="npb-detail-header">
                            <div className="npb-detail-header-left">
                                <span className="npb-detail-title">
                                    {detailProduct.productName}
                                    {detailProduct.productCode && <span className="npb-detail-code">({detailProduct.productCode})</span>}
                                </span>
                            </div>
                            {isAdmin && (
                                !confirmPassive ? (
                                    <button className="npb-detail-head-passive" onClick={() => setConfirmPassive(true)} type="button">
                                        <FormattedMessage id="production.expansion.makePassive" defaultMessage="Bëje pasiv" />
                                    </button>
                                ) : (
                                    <div className="npb-detail-head-confirm">
                                        <button className="npb-detail-head-yes" onClick={makeProductPassive} type="button">
                                            <FormattedMessage id="common.confirm.confirm" defaultMessage="Konfirmo" />
                                        </button>
                                        <button className="npb-detail-head-cancel" onClick={() => setConfirmPassive(false)} type="button">
                                            <FormattedMessage id="common.action.cancel" defaultMessage="Anulo" />
                                        </button>
                                    </div>
                                )
                            )}
                            <button className="npb-detail-close" onClick={closeProductDetail} type="button">&times;</button>
                        </div>

                        <div className="npb-detail-body">
                            <div className="npb-detail-info">
                                <div className="npb-detail-info-item">
                                    <span className="npb-detail-info-label"><FormattedMessage id="production.detail.client" defaultMessage="Klienti" /></span>
                                    <span className="npb-detail-info-value">{detailProduct.clientName || '\u2014'}</span>
                                </div>
                                <div className="npb-detail-info-item">
                                    <span className="npb-detail-info-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></span>
                                    <span className="npb-detail-info-value">{detailProduct.projectName || '\u2014'}</span>
                                </div>
                                <div className="npb-detail-info-item">
                                    <span className="npb-detail-info-label"><FormattedMessage id="production.detail.offerBy" defaultMessage="Ofertën e bëri" /></span>
                                    <span className="npb-detail-info-value">{detailProduct.offerBy || '\u2014'}</span>
                                </div>
                                <div className="npb-detail-info-item">
                                    <span className="npb-detail-info-label"><FormattedMessage id="production.detail.quantity" defaultMessage="Sasia" /></span>
                                    <span className="npb-detail-info-value">{detailProduct.quantity}{detailProduct.unit ? ` ${detailProduct.unit}` : ''}</span>
                                </div>
                                <div className="npb-detail-info-item">
                                    <span className="npb-detail-info-label"><FormattedMessage id="offer.list.column.status" defaultMessage="Statusi" /></span>
                                    <span className="npb-detail-info-value">
                                        {detailProduct.measureStatus === 2
                                            ? <span className="npb-detail-badge npb-detail-badge-green"><FormattedMessage id="production.detail.measureFinal" defaultMessage="Matje finale" /></span>
                                            : <span className="npb-detail-badge npb-detail-badge-amber"><FormattedMessage id="production.detail.measureInitial" defaultMessage="Matje fillestare" /></span>
                                        }
                                    </span>
                                </div>
                                {isAdmin && detailProduct.totalPrice && (
                                    <div className="npb-detail-info-item">
                                        <span className="npb-detail-info-label"><FormattedMessage id="production.detail.value" defaultMessage="Vlera" /></span>
                                        <span className="npb-detail-info-value" style={{ color: '#1e40af', fontWeight: 600 }}>{formatPrice(detailProduct.totalPrice, '€')}</span>
                                    </div>
                                )}
                            </div>

                            {isAdmin && (
                                <div className="npb-detail-dates">
                                    <div className="npb-detail-date-field">
                                        <span className="npb-detail-info-label"><FormattedMessage id="production.detail.start" defaultMessage="Fillimi" /></span>
                                        <DatePicker
                                            selected={detailStartSelected}
                                            onChange={(date: Date | null) => {
                                                const ymd = date ? moment(date).format('YYYY-MM-DD') : '';
                                                setDetailStartDate(ymd);
                                                if (date && detailEndSelected && date.getTime() > detailEndSelected.getTime()) {
                                                    setDetailEndDate(moment(date).format('YYYY-MM-DD'));
                                                }
                                            }}
                                            minDate={detailToday}
                                            openToDate={detailStartView ?? undefined}
                                            onMonthChange={(d: Date) => setDetailStartView(d)}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText={fm('production.detail.selectDate', 'Zgjidh datën')}
                                            className="npb-detail-date-input"
                                            calendarClassName="modern-datepicker-calendar"
                                            isClearable
                                            popperPlacement="bottom-start"
                                            popperModifiers={[{ name: 'preventOverflow', options: { boundary: 'viewport' } }] as any}
                                            popperContainer={({ children }: any) => ReactDOM.createPortal(children, document.body)}
                                            formatWeekDay={weekdayNarrow}
                                            renderCustomHeader={(p: any) => <CalendarHeader {...p} />}
                                            dayClassName={(d: Date) => isSameYMD(d, detailStartSelected) ? '' : 'npb-dp-unselected'}
                                        />
                                    </div>
                                    <div className="npb-detail-date-field">
                                        <span className="npb-detail-info-label"><FormattedMessage id="production.detail.end" defaultMessage="Mbarimi" /></span>
                                        <DatePicker
                                            selected={detailEndSelected}
                                            onChange={(date: Date | null) => setDetailEndDate(date ? moment(date).format('YYYY-MM-DD') : '')}
                                            minDate={detailStartSelected && detailStartSelected.getTime() > detailToday.getTime() ? detailStartSelected : detailToday}
                                            openToDate={detailEndView ?? undefined}
                                            onMonthChange={(d: Date) => setDetailEndView(d)}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText={fm('production.detail.selectDate', 'Zgjidh datën')}
                                            className="npb-detail-date-input"
                                            calendarClassName="modern-datepicker-calendar"
                                            isClearable
                                            popperPlacement="bottom-end"
                                            popperModifiers={[{ name: 'preventOverflow', options: { boundary: 'viewport' } }] as any}
                                            popperContainer={({ children }: any) => ReactDOM.createPortal(children, document.body)}
                                            formatWeekDay={weekdayNarrow}
                                            renderCustomHeader={(p: any) => <CalendarHeader {...p} />}
                                            dayClassName={(d: Date) => isSameYMD(d, detailEndSelected) ? '' : 'npb-dp-unselected'}
                                        />
                                    </div>
                                    {(detailStartDate || detailEndDate) && (
                                        <div className="npb-detail-date-comment">
                                            <textarea
                                                value={detailDateComment}
                                                onChange={e => setDetailDateComment(e.target.value)}
                                                placeholder={fm('production.detail.dateCommentPlaceholder', 'Arsyeja e ndryshimit të datave...')}
                                                rows={2}
                                            />
                                            <button className="npb-detail-date-save" onClick={saveDetailDates} type="button">
                                                <FormattedMessage id="production.detail.saveDates" defaultMessage="Ruaj datat" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {!isAdmin && (
                                <div className="npb-detail-info" style={{ marginBottom: 14 }}>
                                    <div className="npb-detail-info-item">
                                        <span className="npb-detail-info-label"><FormattedMessage id="production.detail.start" defaultMessage="Fillimi" /></span>
                                        <span className="npb-detail-info-value">{detailProduct.start ? moment(detailProduct.start).format('DD-MM-YYYY') : '\u2014'}</span>
                                    </div>
                                    <div className="npb-detail-info-item">
                                        <span className="npb-detail-info-label"><FormattedMessage id="production.detail.end" defaultMessage="Mbarimi" /></span>
                                        <span className="npb-detail-info-value">{detailProduct.end ? moment(detailProduct.end).format('DD-MM-YYYY') : '\u2014'}</span>
                                    </div>
                                </div>
                            )}

                            {detailProduct.calendarComment && (
                                <div style={{
                                    marginBottom: 14,
                                    padding: '10px 14px',
                                    background: '#f8fafc',
                                    borderLeft: '3px solid #94a3b8',
                                    borderRadius: 6,
                                    fontSize: 13,
                                    color: '#334155',
                                    lineHeight: 1.5
                                }}>
                                    <span style={{ fontWeight: 600, marginRight: 8, color: '#475569' }}>
                                        <FormattedMessage id="production.detail.lastDateChangeReason" defaultMessage="Arsyeja e ndryshimit të datave" />:
                                    </span>
                                    {detailProduct.calendarComment}
                                </div>
                            )}

                            {detailProduct.productionStartedAt && (
                                <div className="npb-detail-production-badge">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                    <span>
                                        <FormattedMessage
                                            id="production.detail.inProductionSince"
                                            defaultMessage="Në prodhim që nga: {date}"
                                            values={{ date: detailProduct.productionStartedAt }}
                                        />
                                    </span>
                                </div>
                            )}

                            {detailProduct.totalProcesses > 0 && (() => {
                                const completed = detailProduct.completedProcesses || 0;
                                const total = detailProduct.totalProcesses;
                                const pct = Math.round((completed / total) * 100);
                                const barColor = pct === 100 ? '#22c55e' : pct >= 50 ? '#3b82f6' : '#f59e0b';
                                return (
                                    <div className="npb-detail-progress">
                                        <div className="npb-detail-progress-header">
                                            <span className="npb-detail-progress-label"><FormattedMessage id="production.detail.progress" defaultMessage="Progresi" /></span>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: barColor }}>{completed}/{total} ({pct}%)</span>
                                        </div>
                                        <div className="npb-detail-progress-bar">
                                            <div className="npb-detail-progress-fill" style={{ width: `${pct}%`, background: barColor }} />
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="npb-detail-section">
                                <span className="npb-detail-section-label"><FormattedMessage id="production.detail.productComments" defaultMessage="Komentet e produktit" /></span>
                                <CommentThread
                                    productId={detailProduct.productId}
                                    responsibilityId={null}
                                    materialId={null}
                                    type="comment"
                                    label=""
                                    placeholder={fm('production.detail.commentPlaceholder', 'Shkruaj një koment...')}
                                    isAdmin={isAdmin}
                                />
                            </div>

                            <div className="npb-detail-section">
                                <span className="npb-detail-section-label"><FormattedMessage id="production.detail.files" defaultMessage="Skedarët" /></span>
                                <MikaFileManager
                                    files={detailFiles}
                                    loading={detailFilesLoading}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default withRouter(NewProductionBoard as any);
